import {
  Button,
  Checkbox,
  Counter,
  DropdownMenu,
  IconButton,
  TextField,
} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useCallback, useState} from 'react';
import {createSelector} from 'reselect';

import {WBAgentRunCountFilteredContextSlice} from '../aria/externalBindings/WBAgentContextSlices';
import {useRunSetSummaryQuery} from '../state/graphql/runSetSummaryQuery';
import {useMemoizedSelector} from '../state/hooks';
import * as ViewHooks from '../state/views/hooks';
import * as RunSetActions from '../state/views/runSet/actions';
import type * as RunSetTypes from '../state/views/runSet/types';
import {makeRunSetSelector} from './RunSelector/runSetSelectors';
import {InstrumentedWaveLoader as Loader} from './utility/InstrumentedLoader';

/**
 * Uses createSelector per https://reselect.js.org/api/createSelector
 * Colocated here since it's specific to RunSetDisplay.
 */
const makeRunSetDisplaySelector = (runSetRef: RunSetTypes.Ref) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    name: runSet.name,
    enabled: runSet.enabled,
  }));

interface RunSetSummaryProps {
  active: boolean;
  className?: string | null;
  duplicateRunSet(): void;
  readOnly?: boolean;
  removeRunSet?(): void;
  runSetRef: RunSetTypes.Ref;
  toggleActive(): void;
}

export const RunSetSummary = React.memo(
  ({
    active,
    className,
    duplicateRunSet,
    readOnly,
    removeRunSet,
    runSetRef,
    toggleActive,
  }: RunSetSummaryProps) => {
    const {runSetSummaryQuery, setEnabled, setName} =
      useRunSetSummary(runSetRef);
    // Use useMemoizedSelector directly with the selector factory instead of the
    // convenience hooks in RunSelectorRunSetContext (e.g., usePinnedAndBaseline).
    // This component is called from the reports path where that provider doesn't exist.
    const partialRunSet = useMemoizedSelector(
      makeRunSetDisplaySelector,
      runSetRef
    );

    const [editNameText, setEditNameText] = useState(partialRunSet.name);
    const [isEditingName, setIsEditingName] = useState(false);

    const saveName = useCallback(
      (newName: string) => {
        setName(newName);
        setIsEditingName(false);
      },
      [setName]
    );

    const selectedCount = runSetSummaryQuery.data?.project?.selectedCount;

    return (
      <div
        className={clsx('run-set-summary ', active ? 'active' : '', className)}>
        <WBAgentRunCountFilteredContextSlice count={selectedCount} />
        {isEditingName ? (
          // A mousedown anywhere in the row makes DragHandle arm the ancestor
          // DragSource (draggable=true), which would turn text selection in the
          // field into a native drag. TextField takes no onMouseDown, so the
          // guard lives on this wrapper.
          <div
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}>
            <TextField
              aria-label="Run set name"
              autoFocus_I_READ_THE_JSDOC
              value={editNameText}
              onChange={setEditNameText}
              onBlur={() => saveName(editNameText)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  saveName(editNameText);
                }
              }}
            />
          </div>
        ) : (
          <React.Fragment>
            <Checkbox
              aria-label={`Enable run set ${partialRunSet.name}`}
              isSelected={partialRunSet.enabled}
              onChange={setEnabled}
            />
            <Button variant="ghost" size="sm" onPress={toggleActive}>
              {partialRunSet.name}
            </Button>
            {runSetSummaryQuery.loading ? (
              <Loader name="run-set-summary-loader" size="small" />
            ) : (
              selectedCount != null && (
                <Counter value={selectedCount} aria-label="selected runs" />
              )
            )}
          </React.Fragment>
        )}
        <DropdownMenu.Trigger>
          <IconButton
            aria-label="Run Set Options"
            icon="overflow-horizontal"
            variant="ghost"
          />
          <DropdownMenu.Content placement="bottom start">
            {!readOnly && (
              <DropdownMenu.Item onAction={() => setIsEditingName(true)}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{icon: 'pencil-edit'}}
                />
                <DropdownMenu.ItemLabel>Rename</DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            )}
            {!readOnly && (
              <DropdownMenu.Item onAction={duplicateRunSet}>
                <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'copy'}} />
                <DropdownMenu.ItemLabel>Clone</DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            )}
            {!readOnly && removeRunSet != null && (
              <DropdownMenu.Item onAction={removeRunSet}>
                <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'delete'}} />
                <DropdownMenu.ItemLabel>Delete</DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item onAction={toggleActive}>
              <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'table'}} />
              <DropdownMenu.ItemLabel>
                {active ? 'Minimize' : 'Edit'}
              </DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Trigger>
      </div>
    );
  }
);

function useRunSetSummary(runSetRef: RunSetTypes.Ref) {
  const runSetSummaryQuery = useRunSetSummaryQuery(runSetRef);
  const runSetUpdate = ViewHooks.useViewAction(runSetRef, RunSetActions.update);

  const setEnabled = useCallback(
    (enabled: boolean) => runSetUpdate({enabled}),
    [runSetUpdate]
  );

  const setName = useCallback(
    (name: string) => runSetUpdate({name}),
    [runSetUpdate]
  );

  return {
    runSetSummaryQuery,
    setEnabled,
    setName,
  };
}
